import {
  AlertTriangle,
  CheckCircle2,
  CloudOff,
  LoaderCircle,
  MonitorPlay,
  RefreshCw,
  Unlock,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import {
  playerMediaInteractionEvent,
  requestPlayerMediaInteraction,
  SlideRenderer,
  type MediaControlCommand,
} from "@qassist/slide-renderer";
import {
  getPlayableVideoNodeIds,
  shouldAdvanceAfterVideoProgress,
  staticImageAssetIds,
  type BrowserSessionWireframe,
  type SlideFieldValues,
  type SlideTypeDefinition,
  type VideoPlaybackState,
} from "@qassist/schemas";
import { playerApiBase } from "./api.js";
import { playerIsFullscreen, requestPlayerFullscreen } from "./playerFullscreen.js";
import {
  isPlayerContentRefresh,
  playerSlideInstanceKey,
  playlistHasDisplayContent,
  playlistHasDistinctNextItem,
  shuffledPlaylistItems,
} from "./playerSlideKey.js";
import type { PlatformBranding } from "./adminOperations.js";
import { idbDelete, idbGet, idbSet } from "./idb.js";
import {
  playerCloudHandoffToken,
  playerCloudLinkNeededFragment,
  playerLocationWithoutFragment,
} from "./playerCloudHandoff.js";
import { browserZoomStyle, playerLogicalViewport } from "./playerZoom.js";

interface PlayerItem {
  id: string;
  slideId: string;
  name: string;
  values: SlideFieldValues;
  definition: SlideTypeDefinition;
  durationMs: number;
  advanceOnVideoEnd?: boolean;
  videoLoop?: boolean;
  videoMuted?: boolean;
  entranceAnimation: string;
  exitAnimation: string;
}

interface PlayerConfiguration {
  pollIntervalMs?: number;
  heartbeatIntervalMs?: number;
  tv: {
    id: string;
    name: string;
    status?: string;
    timezone?: string;
    orientation?: "landscape" | "portrait";
    resolutionWidth?: number;
    resolutionHeight?: number;
    zoomPercent?: number;
    backgroundColor?: string;
    backgroundUrl?: string | null;
    controllerEnabled?: boolean;
  };
  clientStatus: string;
  policy: string;
  configurationVersion: number;
  cache: {
    global: number;
    client: number;
    tv: number;
    namespace: string;
  };
  playlist: {
    id: string | null;
    shuffle: boolean;
    overlay: {
      slideId: string;
      name: string;
      values: SlideFieldValues;
      definition: SlideTypeDefinition;
    } | null;
    items: PlayerItem[];
  } | null;
  audio: {
    muted: boolean;
    shuffle: boolean;
    loop: true;
    controllerEnabled?: boolean;
    tracks: Array<{ id: string; name: string; url: string }>;
  } | null;
  branding?: {
    name: string;
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
  };
}

interface PlayerLog {
  at: string;
  level: "info" | "error";
  message: string;
}

interface ControllerCommand extends MediaControlCommand {
  target: "audio" | "youtube";
  targetId: string;
}

function visibleYoutubeControllerTarget(
  item: Pick<PlayerItem, "definition" | "values"> | undefined | null,
  namespace: string,
) {
  if (!item) return null;
  const visit = (
    node: SlideTypeDefinition["layout"],
    path: string,
  ): { targetId: string; label: string; kind: "single" | "playlist" | "list" } | null => {
    if (node.visibleWhen && item.values[node.visibleWhen.field] !== node.visibleWhen.equals)
      return null;
    if ((node.type === "youtube" || node.type === "youtubeList") && node.binding) {
      const value = item.values[node.binding];
      if (
        value &&
        typeof value === "object" &&
        "controllerEnabled" in value &&
        value.controllerEnabled === true
      ) {
        const field = item.definition.fields.find((candidate) => candidate.key === node.binding);
        const url = "url" in value && typeof value.url === "string" ? value.url : "";
        return {
          targetId: `${namespace}:${path}`,
          label: field?.label ?? (node.type === "youtubeList" ? "YouTube List" : "YouTube"),
          kind:
            node.type === "youtubeList" ? "list" : /[?&]list=/.test(url) ? "playlist" : "single",
        };
      }
    }
    for (const [index, child] of (node.children ?? []).entries()) {
      const found = visit(child, `${path}.${index}`);
      if (found) return found;
    }
    return null;
  };
  return visit(item.definition.layout, "root");
}

function withPlaylistPlaybackOrder(
  configuration: PlayerConfiguration,
  previous?: PlayerConfiguration | null,
): PlayerConfiguration {
  if (
    configuration.playlist &&
    previous?.playlist &&
    isPlayerContentRefresh(configuration, previous)
  ) {
    const order = new Map(previous.playlist.items.map((item, index) => [item.id, index]));
    return {
      ...configuration,
      playlist: {
        ...configuration.playlist,
        items: [...configuration.playlist.items].sort(
          (a, b) =>
            (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
            (order.get(b.id) ?? Number.MAX_SAFE_INTEGER),
        ),
      },
    };
  }
  if (!configuration.playlist?.shuffle || configuration.playlist.items.length < 2)
    return configuration;
  return {
    ...configuration,
    playlist: {
      ...configuration.playlist,
      items: shuffledPlaylistItems(configuration.playlist.items),
    },
  };
}

interface BrowserSessionStatus {
  sessionId: string;
  sessionCredential?: string;
  status: "claimed" | "unclaimed";
  tvId: string | null;
  playerCredential?: string;
  identify?: { deviceId: string; requestedAt: string };
  wireframeRequest?: { requestId: string; requestedAt: string };
}

interface VideoPlaybackProgress {
  key: string;
  states: Record<string, VideoPlaybackState>;
  fallbackElapsed: boolean;
}

const DEFAULT_PLAYER_VERSION_POLL_MS = 5_000;
const DEFAULT_PLAYER_HEARTBEAT_MS = 60_000;

class PlayerRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "PlayerRequestError";
  }
}

async function playerFetch<T>(
  path: string,
  credential?: string,
  options: RequestInit = {},
  sameOrigin = false,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (credential) headers.set("Authorization", `Device ${credential}`);
  if (options.body) headers.set("Content-Type", "application/json");
  const response = await fetch(`${sameOrigin ? "" : playerApiBase}${path}`, {
    ...options,
    cache: options.cache ?? "no-store",
    headers,
  });
  const payload = (await response.json()) as {
    data: T | null;
    error: { message: string; code: string } | null;
  };
  if (!response.ok || payload.error || payload.data === null)
    throw new PlayerRequestError(
      payload.error?.message ?? `Player request failed (${response.status})`,
      response.status,
      payload.error?.code,
    );
  return payload.data;
}

async function browserSessionFetch(
  sessionCredential: string | null,
  playerCredential: string | null,
  deviceId: string | null,
): Promise<BrowserSessionStatus> {
  const headers = new Headers();
  if (sessionCredential) headers.set("Authorization", `BrowserSession ${sessionCredential}`);
  if (playerCredential) headers.set("X-QAssist-Player-Authorization", `Device ${playerCredential}`);
  headers.set("X-QAssist-Screen-Width", String(Math.round(window.innerWidth)));
  headers.set("X-QAssist-Screen-Height", String(Math.round(window.innerHeight)));
  if (deviceId) headers.set("X-QAssist-Device-ID", deviceId);
  const response = await fetch(
    `${playerApiBase}/api/v1/player/browser-sessions${sessionCredential ? "/heartbeat" : ""}`,
    {
      method: "POST",
      cache: "no-store",
      headers,
    },
  );
  const payload = (await response.json()) as {
    data: BrowserSessionStatus | null;
    error: { message: string; code: string } | null;
  };
  if (!response.ok || payload.error || !payload.data)
    throw new PlayerRequestError(
      payload.error?.message ?? `Browser session request failed (${response.status})`,
      response.status,
      payload.error?.code,
    );
  return payload.data;
}

function boundedWireframeCoordinate(value: number): number {
  return Math.max(-32_768, Math.min(32_768, Math.round(value)));
}

function wireframeRect(element: Element): { x: number; y: number; width: number; height: number } {
  const rect = element.getBoundingClientRect();
  return {
    x: boundedWireframeCoordinate(rect.left),
    y: boundedWireframeCoordinate(rect.top),
    width: Math.max(0, boundedWireframeCoordinate(rect.width)),
    height: Math.max(0, boundedWireframeCoordinate(rect.height)),
  };
}

function playerWireframe(requestId: string, slideId: string | undefined): BrowserSessionWireframe {
  const root = document.querySelector<HTMLElement>("[data-player-state]");
  const state = root?.dataset.playerState ?? "error";
  const canvases = [...document.querySelectorAll<HTMLElement>(".qr-slide-canvas")].slice(0, 2);
  const nodes: BrowserSessionWireframe["nodes"] = [];
  const canvasSnapshots: BrowserSessionWireframe["canvases"] = [];
  for (const canvas of canvases) {
    const layer = canvas.classList.contains("player-playlist-overlay") ? "overlay" : "slide";
    canvasSnapshots.push({ layer, ...wireframeRect(canvas) });
    for (const element of canvas.querySelectorAll<HTMLElement>(".qr-node")) {
      if (nodes.length >= 160) break;
      const typeClass = [...element.classList].find((name) => name.startsWith("qr-node--"));
      const type = typeClass?.slice("qr-node--".length) ?? "node";
      const rect = wireframeRect(element);
      if (rect.width === 0 || rect.height === 0) continue;
      nodes.push({ index: nodes.length, layer, type, ...rect });
    }
  }
  return {
    requestId,
    capturedAt: new Date().toISOString(),
    viewport: {
      width: Math.max(1, Math.min(32_768, Math.round(window.innerWidth))),
      height: Math.max(1, Math.min(32_768, Math.round(window.innerHeight))),
    },
    state:
      state === "pairing" ||
      state === "loading" ||
      state === "playing" ||
      state === "empty" ||
      state === "disabled" ||
      state === "offline"
        ? state
        : "error",
    slideId: slideId ?? null,
    canvases: canvasSnapshots,
    nodes,
  };
}

async function publishBrowserSessionWireframe(
  sessionCredential: string,
  requestId: string,
  slideId: string | undefined,
): Promise<void> {
  const response = await fetch(`${playerApiBase}/api/v1/player/browser-sessions/wireframe`, {
    method: "POST",
    cache: "no-store",
    headers: {
      Authorization: `BrowserSession ${sessionCredential}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(playerWireframe(requestId, slideId)),
  });
  if (!response.ok) throw new Error(`Wireframe upload failed (${response.status})`);
}

function mediaUrl(value: unknown): string | undefined {
  if (typeof value === "string" && /^https?:\/\//.test(value)) return value;
  if (
    value &&
    typeof value === "object" &&
    "url" in value &&
    typeof (value as { url?: unknown }).url === "string" &&
    /^https?:\/\//.test((value as { url: string }).url)
  )
    return (value as { url: string }).url;
  return undefined;
}

function mediaUrls(value: unknown): string[] {
  const found = new Set<string>();
  const inspect = (candidate: unknown): void => {
    const direct = mediaUrl(candidate);
    if (direct) {
      found.add(direct);
      return;
    }
    if (Array.isArray(candidate)) {
      candidate.forEach(inspect);
      return;
    }
    if (candidate && typeof candidate === "object") Object.values(candidate).forEach(inspect);
  };
  inspect(value);
  return [...found];
}

function findSlideAssetUrls(
  definition: SlideTypeDefinition,
  values: SlideFieldValues,
  fieldTypes: ReadonlySet<string> = new Set([
    "image",
    "logo",
    "video",
    "imageCarousel",
    "contentCarousel",
  ]),
): Set<string> {
  const found = new Set<string>();
  if (fieldTypes.has("image"))
    staticImageAssetIds(definition).forEach((id) => found.add(`/api/v1/media/assets/${id}`));
  for (const field of definition.fields) {
    if (!fieldTypes.has(field.type)) continue;
    mediaUrls(values[field.key]).forEach((url) => found.add(url));
  }
  return found;
}

function findPrefetchAssetUrls(configuration: PlayerConfiguration): Set<string> {
  const found = new Set<string>();
  if (configuration.tv.backgroundUrl) found.add(configuration.tv.backgroundUrl);
  for (const item of configuration.playlist?.items ?? []) {
    findSlideAssetUrls(
      item.definition,
      item.values,
      new Set(["image", "logo", "imageCarousel", "contentCarousel"]),
    ).forEach((url) => found.add(url));
  }
  const overlay = configuration.playlist?.overlay;
  if (overlay)
    findSlideAssetUrls(
      overlay.definition,
      overlay.values,
      new Set(["image", "logo", "imageCarousel", "contentCarousel"]),
    ).forEach((url) => found.add(url));
  return found;
}

type TvAudioConfiguration = NonNullable<PlayerConfiguration["audio"]>;

function shuffledTracks(
  tracks: TvAudioConfiguration["tracks"],
  previousTrackId = "",
): TvAudioConfiguration["tracks"] {
  const next = [...tracks];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [next[index], next[target]] = [next[target]!, next[index]!];
  }
  const first = next[0];
  const second = next[1];
  if (first && second && first.id === previousTrackId) {
    next[0] = second;
    next[1] = first;
  }
  return next;
}

function resolvedPlayerMediaUrl(url: string): string {
  return url.startsWith("/api/") ? `${playerApiBase}${url}` : url;
}

async function playAudioWithWatchdog(
  element: HTMLAudioElement,
  timeoutMs = 1_200,
): Promise<boolean> {
  let playResult: Promise<void> | undefined;
  try {
    playResult = element.play();
  } catch {
    return false;
  }
  if (typeof playResult?.then !== "function") return !element.paused;
  const settled = await Promise.race([
    playResult.then(
      () => true,
      () => false,
    ),
    new Promise<boolean>((resolve) => {
      window.setTimeout(() => resolve(!element.paused), timeoutMs);
    }),
  ]);
  return settled || !element.paused;
}

function TvAudioPlayer({
  audio,
  onLog,
  controlCommand,
  onPermissionRequiredChange,
}: {
  audio: TvAudioConfiguration;
  onLog(level: PlayerLog["level"], message: string): void;
  controlCommand?: ControllerCommand | null;
  onPermissionRequiredChange?: ((required: boolean) => void) | undefined;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackKey = audio.tracks.map((track) => `${track.id}:${track.url}`).join("|");
  const [order, setOrder] = useState(() =>
    audio.shuffle ? shuffledTracks(audio.tracks) : audio.tracks,
  );
  const [trackIndex, setTrackIndex] = useState(0);
  const [audioPermissionRequired, setAudioPermissionRequired] = useState(false);
  const [controllerPaused, setControllerPaused] = useState(false);
  const blockedLogKey = useRef("");
  const playbackAttempt = useRef<Promise<void> | null>(null);
  const playingMutedAfterBlock = useRef(false);
  const handledCommand = useRef("");
  const currentTrack = order[trackIndex] ?? order[0];
  const currentTrackId = currentTrack?.id ?? "";
  const currentTrackName = currentTrack?.name ?? "audio track";

  useEffect(() => {
    setOrder(audio.shuffle ? shuffledTracks(audio.tracks) : audio.tracks);
    setTrackIndex(0);
    setAudioPermissionRequired(false);
    blockedLogKey.current = "";
    playingMutedAfterBlock.current = false;
  }, [audio.shuffle, trackKey]);

  const advanceTrack = useCallback((): void => {
    setTrackIndex((currentIndex) => {
      if (currentIndex + 1 < order.length) return currentIndex + 1;
      const previousTrackId = order[currentIndex]?.id ?? "";
      setOrder(audio.shuffle ? shuffledTracks(audio.tracks, previousTrackId) : audio.tracks);
      return 0;
    });
  }, [audio.shuffle, audio.tracks, order]);
  const previousTrack = useCallback((): void => {
    setTrackIndex((index) => (index > 0 ? index - 1 : Math.max(0, order.length - 1)));
  }, [order.length]);

  useEffect(() => {
    if (
      !controlCommand ||
      controlCommand.target !== "audio" ||
      handledCommand.current === controlCommand.id
    )
      return;
    handledCommand.current = controlCommand.id;
    const element = audioRef.current;
    if (!element) return;
    if (controlCommand.action === "next") advanceTrack();
    else if (controlCommand.action === "previous") previousTrack();
    else if (controlCommand.action === "mute") element.muted = !element.muted;
    else if (element.paused) {
      setControllerPaused(false);
      void playAudioWithWatchdog(element);
    } else {
      setControllerPaused(true);
      element.pause();
    }
  }, [advanceTrack, controlCommand, previousTrack]);

  const attemptPlayback = useCallback(
    async (allowMutedFallback = true): Promise<void> => {
      if (playbackAttempt.current) {
        await playbackAttempt.current;
        return;
      }
      const attempt = (async (): Promise<void> => {
        const element = audioRef.current;
        if (!element || !currentTrackId || controllerPaused) return;
        if (!playingMutedAfterBlock.current) element.muted = audio.muted;
        try {
          if (!(await playAudioWithWatchdog(element))) throw new Error("Audio playback stalled");
          if (audio.muted || !element.muted) {
            playingMutedAfterBlock.current = false;
            setAudioPermissionRequired(false);
            blockedLogKey.current = "";
          }
          return;
        } catch {
          if (!audio.muted && !element.muted && allowMutedFallback) {
            element.muted = true;
            playingMutedAfterBlock.current = true;
            setAudioPermissionRequired(true);
            const key = `${currentTrackId}:sound-blocked`;
            if (blockedLogKey.current !== key) {
              blockedLogKey.current = key;
              onLog(
                "info",
                `The browser blocked automatic audio with sound for ${currentTrackName}; playback is continuing muted until audio is enabled`,
              );
            }
            try {
              if (!(await playAudioWithWatchdog(element)))
                throw new Error("Muted audio playback stalled");
              return;
            } catch {
              playingMutedAfterBlock.current = false;
              onLog("error", `Could not start muted fallback for ${currentTrackName}`);
            }
          }
          const key = `${currentTrackId}:${audio.muted}:playback-failed`;
          if (blockedLogKey.current === key) return;
          blockedLogKey.current = key;
          onLog("error", `Could not autoplay ${currentTrackName}`);
        }
      })();
      playbackAttempt.current = attempt;
      try {
        await attempt;
      } finally {
        if (playbackAttempt.current === attempt) playbackAttempt.current = null;
      }
    },
    [audio.muted, controllerPaused, currentTrackId, currentTrackName, onLog],
  );
  const enableSound = useCallback((): void => {
    playingMutedAfterBlock.current = false;
    if (audioRef.current) audioRef.current.muted = false;
    void attemptPlayback(false);
  }, [attemptPlayback]);

  useEffect(() => {
    const element = audioRef.current;
    if (!element || !currentTrackId) return;
    playingMutedAfterBlock.current = false;
    setAudioPermissionRequired(false);
    element.muted = audio.muted;
    void attemptPlayback();
  }, [attemptPlayback, audio.muted, currentTrackId]);

  useEffect(() => {
    if (audio.muted || !currentTrackId) return;
    const startupTimer = window.setTimeout(() => {
      const element = audioRef.current;
      if (!element?.paused) return;
      element.muted = true;
      playingMutedAfterBlock.current = true;
      setAudioPermissionRequired(true);
      const key = `${currentTrackId}:sound-blocked`;
      if (blockedLogKey.current !== key) {
        blockedLogKey.current = key;
        onLog(
          "info",
          `The browser did not start ${currentTrackName} with sound after refresh; retrying muted playback`,
        );
      }
      void playAudioWithWatchdog(element).then((started) => {
        if (!started) onLog("error", `Could not start muted fallback for ${currentTrackName}`);
      });
    }, 1_600);
    return () => window.clearTimeout(startupTimer);
  }, [audio.muted, currentTrackId, currentTrackName, onLog]);

  useEffect(() => {
    const resume = (): void => {
      void attemptPlayback();
    };
    const resumeWhenVisible = (): void => {
      if (document.visibilityState === "visible") resume();
    };
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resumeWhenVisible);
    const retryTimer = window.setInterval(() => {
      const element = audioRef.current;
      if (element?.paused && !controllerPaused) resume();
    }, 3_000);
    return () => {
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resumeWhenVisible);
      window.clearInterval(retryTimer);
    };
  }, [attemptPlayback, controllerPaused]);

  useEffect(() => {
    if (!audioPermissionRequired || audio.muted) return;
    window.addEventListener("pointerdown", enableSound, { capture: true, once: true });
    window.addEventListener("keydown", enableSound, { capture: true, once: true });
    return () => {
      window.removeEventListener("pointerdown", enableSound, { capture: true });
      window.removeEventListener("keydown", enableSound, { capture: true });
    };
  }, [audio.muted, audioPermissionRequired, enableSound]);

  useEffect(() => {
    if (audio.muted || !currentTrackId) return;
    window.addEventListener(playerMediaInteractionEvent, enableSound);
    return () => window.removeEventListener(playerMediaInteractionEvent, enableSound);
  }, [audio.muted, currentTrackId, enableSound]);

  useEffect(() => {
    onPermissionRequiredChange?.(audioPermissionRequired && !audio.muted);
  }, [audio.muted, audioPermissionRequired, onPermissionRequiredChange]);

  useEffect(() => () => onPermissionRequiredChange?.(false), [onPermissionRequiredChange]);

  if (!currentTrack) return null;
  return (
    <>
      <audio
        key={currentTrack.id}
        ref={audioRef}
        className="player-audio"
        src={resolvedPlayerMediaUrl(currentTrack.url)}
        autoPlay={!controllerPaused}
        muted={audio.muted || audioPermissionRequired}
        loop={order.length === 1}
        preload="auto"
        tabIndex={-1}
        aria-hidden="true"
        onCanPlay={() => {
          if (!controllerPaused) void attemptPlayback();
        }}
        onEnded={advanceTrack}
        onError={() => {
          onLog("error", `Could not play ${currentTrack.name}; moving to the next TV audio track`);
          if (order.length > 1) advanceTrack();
        }}
      />
    </>
  );
}

export function PlayerPage({
  platformBranding,
  deviceId = null,
}: {
  platformBranding: PlatformBranding;
  deviceId?: string | null;
}) {
  const [credential, setCredential] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [configuration, setConfiguration] = useState<PlayerConfiguration | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(true);
  const [error, setError] = useState("");
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [lastVersionCheck, setLastVersionCheck] = useState<string | null>(null);
  const [lastHeartbeat, setLastHeartbeat] = useState<string | null>(null);
  const [browserDeviceId, setBrowserDeviceId] = useState<string | null>(null);
  const [identifiedDeviceId, setIdentifiedDeviceId] = useState<string | null>(null);
  const [logs, setLogs] = useState<PlayerLog[]>([]);
  const [videoPlayback, setVideoPlayback] = useState<VideoPlaybackProgress>({
    key: "",
    states: {},
    fallbackElapsed: false,
  });
  const [transitionPhase, setTransitionPhase] = useState<"entrance" | "exit">("entrance");
  const [controllerCommand, setControllerCommand] = useState<ControllerCommand | null>(null);
  const [playerInteractionConfirmed, setPlayerInteractionConfirmed] = useState(false);
  const [playerFullscreen, setPlayerFullscreen] = useState(playerIsFullscreen);
  const [audioPermissionRequired, setAudioPermissionRequired] = useState(false);
  const [playerViewport, setPlayerViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const acknowledgedControllerCommands = useRef<string[]>([]);
  const lastSync = useRef<string | null>(null);
  const cursorTimer = useRef<number | null>(null);
  const identifyTimer = useRef<number | null>(null);

  const showDeviceIdentifier = useCallback((identifier: string): void => {
    if (identifyTimer.current !== null) window.clearTimeout(identifyTimer.current);
    setIdentifiedDeviceId(identifier);
    identifyTimer.current = window.setTimeout(() => {
      setIdentifiedDeviceId(null);
      identifyTimer.current = null;
    }, 2_000);
  }, []);

  useEffect(
    () => () => {
      if (identifyTimer.current !== null) window.clearTimeout(identifyTimer.current);
    },
    [],
  );
  const exitTimer = useRef<number | null>(null);
  const advancedSlideKey = useRef("");
  const syncInFlight = useRef(false);
  const configurationRetryPending = useRef(false);
  const browserSessionCredential = useRef<string | null>(null);
  const browserSessionSyncInFlight = useRef(false);
  const invalidatedPlayerCredentials = useRef(new Set<string>());
  const heartbeatState = useRef<{ slideId: string | undefined; error: string }>({
    slideId: undefined,
    error: "",
  });
  const credentialKey = "device:current";
  const configurationKey = "configuration:current";
  const browserSessionKey = "browser-session:current";
  const cloudHandoffToken = useRef(playerCloudHandoffToken(window.location.hash));

  useEffect(() => {
    const updateViewport = (): void =>
      setPlayerViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("tv-player-document");
    const retryFullscreen = (): void => {
      if (document.visibilityState === "visible") void requestPlayerFullscreen();
    };
    void requestPlayerFullscreen();
    window.addEventListener("pageshow", retryFullscreen);
    window.addEventListener("focus", retryFullscreen);
    document.addEventListener("visibilitychange", retryFullscreen);
    return () => {
      window.removeEventListener("pageshow", retryFullscreen);
      window.removeEventListener("focus", retryFullscreen);
      document.removeEventListener("visibilitychange", retryFullscreen);
      document.documentElement.classList.remove("tv-player-document");
    };
  }, []);

  useEffect(() => {
    const updateFullscreen = (): void => setPlayerFullscreen(playerIsFullscreen());
    document.addEventListener("fullscreenchange", updateFullscreen);
    document.addEventListener("webkitfullscreenchange", updateFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", updateFullscreen);
      document.removeEventListener("webkitfullscreenchange", updateFullscreen);
    };
  }, []);

  const enableSoundAndFullscreen = useCallback((): void => {
    setPlayerInteractionConfirmed(true);
    const fullscreenRequest = requestPlayerFullscreen();
    requestPlayerMediaInteraction();
    void fullscreenRequest.then(setPlayerFullscreen);
  }, []);

  const log = useCallback((level: PlayerLog["level"], message: string) => {
    setLogs((current) => [...current.slice(-49), { at: new Date().toISOString(), level, message }]);
  }, []);

  const clearPlayerAccess = useCallback(
    async ({
      activeCredential,
      clearBrowserSession = false,
      message,
    }: {
      activeCredential: string | null;
      clearBrowserSession?: boolean;
      message: string;
    }): Promise<void> => {
      if (activeCredential) invalidatedPlayerCredentials.current.add(activeCredential);
      const keys = [credentialKey, configurationKey];
      if (clearBrowserSession) {
        keys.push(browserSessionKey);
        browserSessionCredential.current = null;
        setBrowserDeviceId(null);
      }
      await Promise.all(keys.map((key) => idbDelete(key)));
      setCredential(null);
      setConfiguration(null);
      setCurrentIndex(0);
      setTransitionPhase("entrance");
      setVideoPlayback({ key: "", states: {}, fallbackElapsed: false });
      setLastVersionCheck(null);
      setLastHeartbeat(null);
      lastSync.current = null;
      advancedSlideKey.current = "";
      configurationRetryPending.current = false;
      setSyncing(false);
      setError(message);
      log("error", message);
    },
    [browserSessionKey, configurationKey, credentialKey, log],
  );

  const sync = useCallback(
    async (activeCredential: string): Promise<void> => {
      if (invalidatedPlayerCredentials.current.has(activeCredential)) return;
      if (syncInFlight.current) return;
      syncInFlight.current = true;
      setSyncing(true);
      try {
        const next = withPlaylistPlaybackOrder(
          await playerFetch<PlayerConfiguration>("/api/v1/player/configuration", activeCredential),
          configuration,
        );
        const urls = [...findPrefetchAssetUrls(next)];
        const results = await Promise.allSettled(
          urls.slice(0, 40).map(async (url) => {
            const response = await fetch(url, { cache: "force-cache", mode: "no-cors" });
            if (!response.ok && response.type !== "opaque")
              throw new Error(`Asset unavailable: ${response.status}`);
          }),
        );
        const failedCriticalAssets = results.filter(
          (result) => result.status === "rejected",
        ).length;
        if (failedCriticalAssets && configuration) {
          throw new Error(
            `${failedCriticalAssets} replacement assets were unavailable; retained working cache`,
          );
        }
        if (invalidatedPlayerCredentials.current.has(activeCredential)) return;
        await idbSet(configurationKey, next);
        if (invalidatedPlayerCredentials.current.has(activeCredential)) {
          await idbDelete(configurationKey);
          return;
        }
        setConfiguration(next);
        if (!isPlayerContentRefresh(next, configuration)) setCurrentIndex(0);
        configurationRetryPending.current = false;
        lastSync.current = new Date().toISOString();
        setError("");
        log("info", `Configuration ${next.configurationVersion} activated atomically`);
      } catch (reason) {
        const message = reason instanceof Error ? reason.message : "Configuration sync failed";
        configurationRetryPending.current = true;
        setError(message);
        log("error", message);
        if (
          reason instanceof PlayerRequestError &&
          (reason.status === 401 || reason.status === 404)
        ) {
          await clearPlayerAccess({
            activeCredential,
            message:
              "This television pairing is no longer valid. Enter the current six-digit code.",
          });
          return;
        }
        const cached = await idbGet<PlayerConfiguration>(configurationKey);
        if (cached) setConfiguration(cached);
      } finally {
        syncInFlight.current = false;
        setSyncing(false);
      }
    },
    [clearPlayerAccess, configuration, configurationKey, log],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const handoffToken = cloudHandoffToken.current;
      cloudHandoffToken.current = null;
      if (handoffToken) {
        window.history.replaceState(
          window.history.state,
          "",
          playerLocationWithoutFragment(window.location),
        );
        try {
          const result = await playerFetch<{ credential: string; tvId: string }>(
            "/api/v1/player/cloud-handoff",
            undefined,
            { method: "POST", body: JSON.stringify({ token: handoffToken }) },
            true,
          );
          if (cancelled) return;
          await idbSet(credentialKey, result.credential);
          if (cancelled) return;
          setCredential(result.credential);
          await sync(result.credential);
          return;
        } catch (reason) {
          if (cancelled) return;
          setError(
            reason instanceof Error
              ? reason.message
              : "The automatic display connection could not be completed.",
          );
        }
      }
      const savedCredential = await idbGet<string>(credentialKey);
      if (cancelled) return;
      if (savedCredential) {
        setCredential(savedCredential);
        await sync(savedCredential);
      } else {
        setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [credentialKey]);

  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    const heartbeat = async (): Promise<void> => {
      if (browserSessionSyncInFlight.current) return;
      browserSessionSyncInFlight.current = true;
      try {
        const savedSessionCredential =
          browserSessionCredential.current ?? (await idbGet<string>(browserSessionKey));
        if (cancelled) return;
        browserSessionCredential.current = savedSessionCredential;
        const result = await browserSessionFetch(savedSessionCredential, credential, deviceId);
        if (cancelled) return;
        setBrowserDeviceId(result.sessionId);
        if (result.identify) showDeviceIdentifier(result.identify.deviceId);
        if (result.sessionCredential) {
          browserSessionCredential.current = result.sessionCredential;
          await idbSet(browserSessionKey, result.sessionCredential);
        }
        const activeSessionCredential = result.sessionCredential ?? savedSessionCredential;
        if (result.wireframeRequest && activeSessionCredential) {
          try {
            await publishBrowserSessionWireframe(
              activeSessionCredential,
              result.wireframeRequest.requestId,
              heartbeatState.current.slideId,
            );
          } catch (reason) {
            log("error", reason instanceof Error ? reason.message : "Wireframe upload failed");
          }
        }
        if (result.playerCredential && result.playerCredential !== credential) {
          await idbSet(credentialKey, result.playerCredential);
          if (cancelled) return;
          setCredential(result.playerCredential);
          await sync(result.playerCredential);
        } else if (credential && result.tvId && configuration?.tv.id !== result.tvId) {
          await sync(credential);
        } else if (credential && result.status === "unclaimed") {
          await clearPlayerAccess({
            activeCredential: credential,
            message: "Display access was revoked. This browser is ready to be assigned again.",
          });
        }
      } catch (reason) {
        if (
          reason instanceof PlayerRequestError &&
          reason.code === "BROWSER_SESSION_UNAUTHENTICATED"
        ) {
          await clearPlayerAccess({
            activeCredential: credential,
            clearBrowserSession: true,
            message: "Display access was revoked. This browser is ready to be assigned again.",
          });
        } else {
          log(
            "error",
            reason instanceof Error ? reason.message : "Browser session heartbeat failed",
          );
        }
      } finally {
        browserSessionSyncInFlight.current = false;
      }
    };
    const interval = window.setInterval(() => void heartbeat(), DEFAULT_PLAYER_VERSION_POLL_MS);
    const heartbeatWhenVisible = (): void => {
      if (document.visibilityState === "visible") void heartbeat();
    };
    window.addEventListener("focus", heartbeatWhenVisible);
    window.addEventListener("online", heartbeatWhenVisible);
    window.addEventListener("pageshow", heartbeatWhenVisible);
    document.addEventListener("visibilitychange", heartbeatWhenVisible);
    void heartbeat();
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", heartbeatWhenVisible);
      window.removeEventListener("online", heartbeatWhenVisible);
      window.removeEventListener("pageshow", heartbeatWhenVisible);
      document.removeEventListener("visibilitychange", heartbeatWhenVisible);
    };
  }, [
    browserSessionKey,
    clearPlayerAccess,
    configuration?.tv.id,
    credential,
    credentialKey,
    deviceId,
    log,
    online,
    sync,
    showDeviceIdentifier,
  ]);

  useEffect(() => {
    if (!deviceId || syncing || credential || window.location.hash) return;
    window.location.hash = playerCloudLinkNeededFragment.slice(1);
    const clearMarker = window.setTimeout(() => {
      if (window.location.hash === playerCloudLinkNeededFragment)
        window.history.replaceState(
          window.history.state,
          "",
          playerLocationWithoutFragment(window.location),
        );
    }, 1_000);
    return () => window.clearTimeout(clearMarker);
  }, [credential, deviceId, syncing]);

  useEffect(() => {
    const onOnline = (): void => setOnline(true);
    const onOffline = (): void => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    const shortcut = (event: KeyboardEvent): void => {
      if (
        event.ctrlKey &&
        event.altKey &&
        event.shiftKey &&
        event.key.toLowerCase() === "q" &&
        credential
      ) {
        event.preventDefault();
        setDiagnosticsOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, [credential]);

  useEffect(() => {
    if (!credential || !configuration) return;
    let checkInFlight = false;
    const checkForUpdates = async (): Promise<void> => {
      if (checkInFlight) return;
      checkInFlight = true;
      try {
        const version = await playerFetch<{
          configurationVersion: number;
          cacheVersion: string;
        }>("/api/v1/player/configuration/version", credential);
        setLastVersionCheck(new Date().toISOString());
        if (
          configurationRetryPending.current ||
          ((configuration.playlist?.items.some((item) =>
            item.definition.fields.some((field) => field.type === "wordpressPosts"),
          ) ||
            configuration.playlist?.overlay?.definition.fields.some(
              (field) => field.type === "wordpressPosts",
            )) &&
            Date.now() - Date.parse(lastSync.current ?? "1970-01-01") >= 60_000) ||
          version.configurationVersion !== configuration.configurationVersion ||
          version.cacheVersion !== configuration.cache.namespace
        )
          await sync(credential);
      } catch (reason) {
        if (
          reason instanceof PlayerRequestError &&
          (reason.status === 401 || reason.status === 404)
        ) {
          await clearPlayerAccess({
            activeCredential: credential,
            message: "Display access was revoked. This browser is ready to be assigned again.",
          });
        } else {
          log("error", reason instanceof Error ? reason.message : "Version poll failed");
        }
      } finally {
        checkInFlight = false;
      }
    };
    const checkWhenVisible = (): void => {
      if (document.visibilityState === "visible") void checkForUpdates();
    };
    const interval = window.setInterval(
      () => void checkForUpdates(),
      configuration.pollIntervalMs ?? DEFAULT_PLAYER_VERSION_POLL_MS,
    );
    window.addEventListener("focus", checkWhenVisible);
    window.addEventListener("online", checkWhenVisible);
    window.addEventListener("pageshow", checkWhenVisible);
    document.addEventListener("visibilitychange", checkWhenVisible);
    void checkForUpdates();
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", checkWhenVisible);
      window.removeEventListener("online", checkWhenVisible);
      window.removeEventListener("pageshow", checkWhenVisible);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [
    configuration?.cache.namespace,
    configuration?.configurationVersion,
    configuration?.pollIntervalMs,
    credential,
    clearPlayerAccess,
    log,
    sync,
  ]);

  const items = configuration?.playlist?.items ?? [];
  const current = items[currentIndex % Math.max(1, items.length)];
  const next = items[(currentIndex + 1) % Math.max(1, items.length)];
  const overlay = configuration?.playlist?.overlay;
  const hasDisplayContent = playlistHasDisplayContent(current, overlay);
  const youtubeControllerTarget = useMemo(
    () =>
      visibleYoutubeControllerTarget(current, "main") ??
      visibleYoutubeControllerTarget(configuration?.playlist?.overlay, "overlay"),
    [configuration?.playlist?.overlay, current],
  );
  heartbeatState.current = { slideId: current?.slideId, error };
  const currentKey = current
    ? playerSlideInstanceKey(configuration?.cache.namespace, current.id)
    : "";
  const playableVideoIds = useMemo(
    () => (current ? getPlayableVideoNodeIds(current.definition, current.values) : []),
    [current],
  );
  const shouldWaitForVideo = Boolean(current?.advanceOnVideoEnd) && playableVideoIds.length > 0;
  const currentDurationMs = current?.durationMs;
  const currentExitAnimation = current?.exitAnimation;

  useEffect(() => {
    if (!credential || !configuration) return;
    let inFlight = false;
    const poll = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      const acknowledgedCommandIds = acknowledgedControllerCommands.current.splice(0);
      try {
        const result = await playerFetch<{
          commands: Array<{
            id: string;
            target: "audio" | "youtube";
            targetId: string | null;
            action: ControllerCommand["action"];
          }>;
        }>("/api/v1/player/controller/state", credential, {
          method: "POST",
          body: JSON.stringify({
            slideId: current?.slideId ?? null,
            youtube: youtubeControllerTarget,
            audioAvailable: Boolean(
              configuration.audio?.tracks.length && configuration.audio.controllerEnabled,
            ),
            acknowledgedCommandIds,
          }),
        });
        const command = result.commands[0];
        if (command) {
          setControllerCommand({ ...command, targetId: command.targetId ?? "audio" });
          acknowledgedControllerCommands.current.push(command.id);
        }
      } catch (reason) {
        acknowledgedControllerCommands.current.unshift(...acknowledgedCommandIds);
        log("error", reason instanceof Error ? reason.message : "Controller sync failed");
      } finally {
        inFlight = false;
      }
    };
    void poll();
    const interval = window.setInterval(() => void poll(), 1_000);
    return () => window.clearInterval(interval);
  }, [configuration, credential, current?.slideId, log, youtubeControllerTarget]);
  const advanceCurrent = useCallback(
    (key: string): void => {
      if (!playlistHasDistinctNextItem(items.length)) return;
      if (advancedSlideKey.current === key) return;
      advancedSlideKey.current = key;
      if (current?.exitAnimation && current.exitAnimation !== "none") {
        setTransitionPhase("exit");
        exitTimer.current = window.setTimeout(() => {
          setCurrentIndex((index) => index + 1);
        }, 500);
      } else {
        setCurrentIndex((index) => index + 1);
      }
    },
    [current?.exitAnimation, items.length],
  );
  const updateVideoPlayback = useCallback(
    (videoId: string, state: VideoPlaybackState): void => {
      setVideoPlayback((progress) => ({
        key: currentKey,
        states: {
          ...(progress.key === currentKey ? progress.states : {}),
          [videoId]: state,
        },
        fallbackElapsed: progress.key === currentKey ? progress.fallbackElapsed : false,
      }));
    },
    [currentKey],
  );

  useEffect(() => {
    if (exitTimer.current) window.clearTimeout(exitTimer.current);
    exitTimer.current = null;
    advancedSlideKey.current = "";
    setTransitionPhase("entrance");
    setVideoPlayback({ key: currentKey, states: {}, fallbackElapsed: false });
  }, [currentKey]);

  useEffect(() => {
    if (currentDurationMs === undefined || document.visibilityState === "hidden") return;
    const exitLeadMs =
      !shouldWaitForVideo && currentExitAnimation && currentExitAnimation !== "none" ? 500 : 0;
    const timeout = window.setTimeout(
      () => {
        if (shouldWaitForVideo) {
          setVideoPlayback((progress) => ({
            key: currentKey,
            states: progress.key === currentKey ? progress.states : {},
            fallbackElapsed: true,
          }));
        } else {
          advanceCurrent(currentKey);
        }
      },
      Math.max(0, currentDurationMs - exitLeadMs),
    );
    return () => window.clearTimeout(timeout);
  }, [advanceCurrent, currentDurationMs, currentExitAnimation, currentKey, shouldWaitForVideo]);

  useEffect(
    () => () => {
      if (exitTimer.current) window.clearTimeout(exitTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (
      !current ||
      !shouldWaitForVideo ||
      videoPlayback.key !== currentKey ||
      !playableVideoIds.length
    )
      return;
    if (
      shouldAdvanceAfterVideoProgress(
        playableVideoIds,
        videoPlayback.states,
        videoPlayback.fallbackElapsed,
      )
    )
      advanceCurrent(currentKey);
  }, [advanceCurrent, current, currentKey, playableVideoIds, shouldWaitForVideo, videoPlayback]);

  useEffect(() => {
    if (next) {
      findSlideAssetUrls(next.definition, next.values).forEach((url) => {
        const image = new window.Image();
        image.src = url;
      });
    }
  }, [next]);

  useEffect(() => {
    if (!credential || !configuration) return;
    let heartbeatInFlight = false;
    const heartbeat = (): void => {
      if (heartbeatInFlight) return;
      heartbeatInFlight = true;
      void playerFetch("/api/v1/player/heartbeat", credential, {
        method: "POST",
        body: JSON.stringify({
          playerVersion: "0.1.0",
          playlistId: configuration.playlist?.id,
          slideId: heartbeatState.current.slideId,
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          orientation: configuration.tv.orientation ?? "landscape",
          cacheVersion: configuration.cache.tv,
          syncResult: heartbeatState.current.error ? "cached" : "ok",
          ...(heartbeatState.current.error ? { errorCode: "SYNC_WARNING" } : {}),
        }),
      })
        .then(() => setLastHeartbeat(new Date().toISOString()))
        .catch((reason: unknown) =>
          log("error", reason instanceof Error ? reason.message : "Heartbeat failed"),
        )
        .finally(() => {
          heartbeatInFlight = false;
        });
    };
    const heartbeatWhenVisible = (): void => {
      if (document.visibilityState === "visible") heartbeat();
    };
    heartbeat();
    const interval = window.setInterval(
      heartbeat,
      configuration.heartbeatIntervalMs ?? DEFAULT_PLAYER_HEARTBEAT_MS,
    );
    window.addEventListener("focus", heartbeatWhenVisible);
    window.addEventListener("online", heartbeatWhenVisible);
    window.addEventListener("pageshow", heartbeatWhenVisible);
    document.addEventListener("visibilitychange", heartbeatWhenVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", heartbeatWhenVisible);
      window.removeEventListener("online", heartbeatWhenVisible);
      window.removeEventListener("pageshow", heartbeatWhenVisible);
      document.removeEventListener("visibilitychange", heartbeatWhenVisible);
    };
  }, [
    configuration?.cache.tv,
    configuration?.heartbeatIntervalMs,
    configuration?.playlist?.id,
    configuration?.tv.orientation,
    credential,
    log,
  ]);

  useEffect(() => {
    const showCursor = (): void => {
      document.body.classList.remove("cursor-hidden");
      if (cursorTimer.current) window.clearTimeout(cursorTimer.current);
      cursorTimer.current = window.setTimeout(
        () => document.body.classList.add("cursor-hidden"),
        3000,
      );
    };
    window.addEventListener("mousemove", showCursor);
    showCursor();
    return () => {
      window.removeEventListener("mousemove", showCursor);
      if (cursorTimer.current) window.clearTimeout(cursorTimer.current);
      document.body.classList.remove("cursor-hidden");
    };
  }, []);

  async function activate(event: FormEvent): Promise<void> {
    event.preventDefault();
    enableSoundAndFullscreen();
    setSyncing(true);
    setError("");
    try {
      const result = await playerFetch<{ credential: string; tvId: string }>(
        "/api/v1/player/activate",
        undefined,
        { method: "POST", body: JSON.stringify({ code }) },
      );
      await idbSet(credentialKey, result.credential);
      setCredential(result.credential);
      await sync(result.credential);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Activation failed.");
      setSyncing(false);
    }
  }

  async function revokeLocally(): Promise<void> {
    await Promise.all([
      idbDelete(credentialKey),
      idbDelete(configurationKey),
      idbDelete(browserSessionKey),
    ]);
    window.location.reload();
  }

  const branding = configuration?.branding ?? {
    name: configuration?.tv.name ?? platformBranding.name,
    logoUrl: platformBranding.logoUrl,
    primaryColor: platformBranding.primaryColor,
    secondaryColor: platformBranding.secondaryColor,
  };
  const playerBrandStyle = {
    "--brand-primary": branding.primaryColor,
    "--brand-secondary": branding.secondaryColor,
    "--player-background-color": configuration?.tv.backgroundColor ?? "#000000",
    "--player-background-image": configuration?.tv.backgroundUrl
      ? `url("${configuration.tv.backgroundUrl.replaceAll('"', "%22")}")`
      : "none",
  } as CSSProperties;
  const logicalViewport = playerLogicalViewport(
    playerViewport.width,
    playerViewport.height,
    current?.definition.canvas.width ?? overlay?.definition.canvas.width ?? 1960,
  );
  const diagnosticData = useMemo(
    () => ({
      playerVersion: "0.1.0",
      tvId: configuration?.tv.id ?? "unpaired",
      tvName: configuration?.tv.name ?? "unpaired",
      lastConfigurationSync: lastSync.current,
      lastVersionCheck,
      lastHeartbeat,
      configurationVersion: configuration?.configurationVersion,
      cacheNamespace: configuration?.cache.namespace,
      currentSlide: current?.name,
      nextSlide: next?.name,
      currentAudio:
        configuration?.audio?.tracks.length && !configuration.audio.muted
          ? `${configuration.audio.tracks.length} autoplay track${configuration.audio.tracks.length === 1 ? "" : "s"}`
          : configuration?.audio?.muted
            ? "muted"
            : "none",
      screen: `${window.screen.width}×${window.screen.height}`,
      online,
      userAgent: navigator.userAgent,
    }),
    [configuration, current?.name, lastHeartbeat, lastVersionCheck, next?.name, online],
  );
  const deviceIdentifier = identifiedDeviceId ? (
    <div className="player-device-identifier" role="status" aria-live="polite">
      Device ID {identifiedDeviceId}
    </div>
  ) : null;

  if (syncing && !configuration)
    return (
      <main className="player-connection" data-player-state="loading" style={playerBrandStyle}>
        {deviceIdentifier}
        <LoaderCircle className="spin" size={38} />
        <h1>Starting {platformBranding.name}</h1>
        <p>Checking device credentials and cached content…</p>
      </main>
    );

  if (!credential) {
    if (!online) {
      return (
        <main className="player-connection" data-player-state="offline" style={playerBrandStyle}>
          {deviceIdentifier}
          <span className="pairing-icon" aria-hidden="true">
            <CloudOff size={38} />
          </span>
          <span className="eyebrow">Q-Assist TV</span>
          <h1>No internet connection</h1>
          <p>
            Offline playback is not ready on this display. The player will open automatically as
            soon as the internet connection returns.
          </p>
          <div className="pairing-status">
            <span>
              <WifiOff size={17} />
            </span>
            Waiting for internet
          </div>
        </main>
      );
    }
    return (
      <main className="player-pairing" data-player-state="pairing" style={playerBrandStyle}>
        {deviceIdentifier}
        <section className="pairing-card">
          <span className="pairing-icon">
            {platformBranding.logoUrl ? (
              <img src={platformBranding.logoUrl} alt="" />
            ) : (
              <MonitorPlay size={34} />
            )}
          </span>
          <span className="eyebrow">{platformBranding.name} device pairing</span>
          <h1>Connect this display</h1>
          <p>Enter the six-digit activation code shown in the client dashboard.</p>
          {deviceId || browserDeviceId ? (
            <p className="pairing-device-id">
              Device ID <strong>{deviceId ?? browserDeviceId}</strong>
            </p>
          ) : null}
          <form onSubmit={(event) => void activate(event)}>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              aria-label="Six-digit activation code"
              autoFocus
            />
            <button
              type="submit"
              className="button button--primary button--wide"
              disabled={code.length !== 6 || syncing}
            >
              {syncing ? "Connecting…" : "Activate display"}
            </button>
          </form>
          {error ? <div className="form-error">{error}</div> : null}
          <div className="pairing-status">
            <span className={online ? "is-online" : ""}>
              {online ? <Wifi size={17} /> : <WifiOff size={17} />}
            </span>
            {online ? "Internet connection available" : "Waiting for a network connection"}
          </div>
        </section>
      </main>
    );
  }

  if (
    configuration &&
    configuration.clientStatus !== "active" &&
    configuration.policy !== "continue_cached"
  )
    return (
      <main className="player-connection" data-player-state="disabled" style={playerBrandStyle}>
        {deviceIdentifier}
        <AlertTriangle size={44} />
        <h1>
          {configuration.policy === "show_expired_screen"
            ? "Subscription expired"
            : "Display disabled"}
        </h1>
        <p>{branding.name} cannot retrieve new content. Contact the workspace administrator.</p>
      </main>
    );

  return (
    <main
      className="player-stage"
      data-player-state={
        hasDisplayContent ? "playing" : configuration ? "empty" : error ? "error" : "loading"
      }
      style={playerBrandStyle}
    >
      {deviceIdentifier}
      {configuration?.audio?.tracks.length ? (
        <TvAudioPlayer
          audio={configuration.audio}
          onLog={log}
          controlCommand={controllerCommand}
          onPermissionRequiredChange={setAudioPermissionRequired}
        />
      ) : null}
      {configuration &&
      (!playerInteractionConfirmed || !playerFullscreen || audioPermissionRequired) ? (
        <button
          className="player-unlock-control"
          type="button"
          aria-label="Unlock sound and fullscreen"
          title="Unlock sound and fullscreen"
          onClick={enableSoundAndFullscreen}
        >
          <Unlock aria-hidden="true" />
        </button>
      ) : null}
      {hasDisplayContent ? (
        <div
          className="player-canvas-fit"
          style={{
            width: logicalViewport.width,
            height: logicalViewport.height,
            transform: `scale(${logicalViewport.scale})`,
          }}
        >
          <div
            className="player-content-zoom"
            style={browserZoomStyle(configuration?.tv.zoomPercent ?? 100)}
          >
            {current ? (
              <SlideRenderer
                key={currentKey}
                definition={current.definition}
                values={current.values}
                orientation={configuration?.tv.orientation ?? "landscape"}
                timezone={configuration?.tv.timezone}
                className={`player-slide player-transition--${
                  transitionPhase === "exit" ? current.exitAnimation : current.entranceAnimation
                }`}
                ariaLabel={current.name}
                waitForVideoEnd={shouldWaitForVideo}
                videoLoop={current.videoLoop ?? true}
                videoMuted={current.videoMuted ?? true}
                onVideoPlaybackStateChange={updateVideoPlayback}
                mediaControlCommand={
                  controllerCommand?.target === "youtube" ? controllerCommand : null
                }
                mediaControlNamespace="main"
              />
            ) : null}
            {overlay ? (
              <SlideRenderer
                definition={overlay.definition}
                values={overlay.values}
                orientation={configuration?.tv.orientation ?? "landscape"}
                timezone={configuration?.tv.timezone}
                className="player-playlist-overlay"
                ariaLabel={overlay.name}
                videoLoop
                videoMuted
                mediaControlCommand={
                  controllerCommand?.target === "youtube" ? controllerCommand : null
                }
                mediaControlNamespace="overlay"
              />
            ) : null}
          </div>
        </div>
      ) : (
        <section className="player-connection player-connection--embedded">
          <CloudOff size={42} />
          {branding.logoUrl ? (
            <img className="player-brand-logo" src={branding.logoUrl} alt={branding.name} />
          ) : null}
          <span className="eyebrow">{branding.name}</span>
          <h1>{configuration?.tv.name ?? branding.name}</h1>
          <p>No scheduled slides are available. The player will refresh automatically.</p>
        </section>
      )}
      {!online || error ? (
        <div className="offline-badge">
          {online ? <RefreshCw size={15} /> : <WifiOff size={15} />}
          {online ? "Using last valid content" : "Offline playback"}
        </div>
      ) : null}
      {diagnosticsOpen ? (
        <aside className="diagnostics">
          <header>
            <div>
              <span className="eyebrow">Protected diagnostics</span>
              <h2>Player state</h2>
            </div>
            <button onClick={() => setDiagnosticsOpen(false)} aria-label="Close diagnostics">
              <X />
            </button>
          </header>
          <dl>
            {Object.entries(diagnosticData).map(([key, value]) => (
              <div key={key}>
                <dt>{key.replace(/([A-Z])/g, " $1")}</dt>
                <dd>{String(value ?? "—")}</dd>
              </div>
            ))}
          </dl>
          <div className="diagnostic-status">
            <CheckCircle2 size={17} /> Ctrl + Alt + Shift + Q toggles this panel
          </div>
          <div className="diagnostic-logs">
            {logs.slice(-8).map((entry) => (
              <p
                key={`${entry.at}-${entry.message}`}
                className={entry.level === "error" ? "is-error" : ""}
              >
                <time>{new Date(entry.at).toLocaleTimeString()}</time>
                {entry.message}
              </p>
            ))}
          </div>
          <button
            className="button button--secondary button--wide"
            onClick={() => void revokeLocally()}
          >
            Forget local device credential
          </button>
        </aside>
      ) : null}
    </main>
  );
}
